import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  subscribeToSession,
  type SessionState,
  type SessionStatus,
  type SessionUser,
} from '@/services/session';

interface SessionContextValue {
  status: SessionStatus;
  isAuthReady: boolean;
  isAuthenticated: boolean;
  user: SessionUser | null;
  uid: string | null;
  email: string | null;
}

const SessionContext = createContext<SessionContextValue | null>(null);

interface SessionProviderProps {
  children: ReactNode;
}

export function SessionProvider({ children }: SessionProviderProps) {
  const [session, setSession] = useState<SessionState>(() => ({
    status: 'loading',
    user: null,
  }));

  useEffect(() => subscribeToSession(setSession), []);

  const value = useMemo<SessionContextValue>(() => {
    const isAuthenticated = session.status === 'authenticated' && session.user !== null;

    return {
      status: session.status,
      isAuthReady: session.status !== 'loading',
      isAuthenticated,
      user: session.user,
      uid: session.user?.uid ?? null,
      email: session.user?.email ?? null,
    };
  }, [session]);

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within SessionProvider');
  }
  return context;
}
